import { downloadText } from "@/features/preflight/lib/download";

import type { PersonaOpportunityDisplayRow } from "./persona-opportunity-row-model";
import { formatPercent } from "./persona-opportunity-row-model";

/** Characters that make a spreadsheet treat a cell as a formula (CSV injection). */
const FORMULA_PREFIXES = ["=", "+", "-", "@"];

function csvCell(value: string | number | null | undefined): string {
  const raw = value == null ? "" : String(value);
  const trimmed = raw.trimStart();
  const guarded = FORMULA_PREFIXES.some((prefix) => trimmed.startsWith(prefix))
    ? `'${raw}`
    : raw;
  return `"${guarded.replace(/"/g, '""')}"`;
}

const CSV_HEADERS = [
  "Persona",
  "Angle",
  "Status",
  "Creatives",
  "Competitors running",
  "Competitors total",
  "Stick rate",
  "Duplicated",
  "My investment share",
  "Market investment share",
  "Investment index",
  "Competitor",
  "Supporting ad URL",
  "Supporting ad days live",
  "Supporting ad duplications",
] as const;

export interface PersonaOpportunityExportRow {
  personaTitle: string;
  row: PersonaOpportunityDisplayRow;
}

function statusLabel(status: PersonaOpportunityDisplayRow["status"]): string {
  switch (status) {
    case "NEW":
      return "New";
    case "SCRIPTS_GENERATED":
      return "Scripts generated";
    case "BRIEFS_GENERATED":
      return "Briefs generated";
    case "SCRIPTS_AND_BRIEFS_GENERATED":
      return "Scripts + briefs generated";
    case "SENT_TO_VENDOR":
      return "Sent to vendor";
    default:
      return status;
  }
}

export function buildPersonaOpportunitiesCsv(rows: PersonaOpportunityExportRow[]): string {
  const lines = [
    CSV_HEADERS.map((header) => csvCell(header)).join(","),
    ...rows.map(({ personaTitle, row }) =>
      [
        csvCell(personaTitle),
        csvCell(row.angle),
        csvCell(statusLabel(row.status)),
        csvCell(row.creativesCount),
        csvCell(row.competitorsRunningCount),
        csvCell(row.competitorsTotal),
        csvCell(formatPercent(row.stickRatePct)),
        csvCell(formatPercent(row.duplicatedPct)),
        csvCell(formatPercent(row.myInvestmentSharePct)),
        csvCell(formatPercent(row.marketInvestmentSharePct)),
        csvCell(row.investmentIndex),
        csvCell(row.competitorName),
        csvCell(row.mediaUrl),
        csvCell(row.daysLive),
        csvCell(row.duplications),
      ].join(","),
    ),
  ];
  return lines.join("\n");
}

export function exportPersonaOpportunitiesCsv(
  rows: PersonaOpportunityExportRow[],
  filename: string,
): void {
  downloadText(buildPersonaOpportunitiesCsv(rows), filename, "text/csv;charset=utf-8");
}
